import { gql } from '@apollo/client';

/** Reach (Calendar · Channels): queries, channel names and dates. */

export const REACH_POST_FIELDS = `
  id
  platform
  summary
  title
  reachStatus
  reviewStatus
  scheduleTime
  proposedTime
  socialPublishDate
  socialProfileId
`;

export const REACH_CALENDAR = gql`
  query ReachCalendar($accountId: ID!, $from: String!, $to: String!) {
    reachCalendar(accountId: $accountId, from: $from, to: $to) {
      posts { ${REACH_POST_FIELDS} }
      waiting { ${REACH_POST_FIELDS} }
    }
  }
`;

export const APPROVE_SOCIAL_POST = gql`
  mutation ApproveSocialPost($id: ID!) {
    approveSocialPost(id: $id) { ${REACH_POST_FIELDS} }
  }
`;

export const SKIP_SOCIAL_POST = gql`
  mutation SkipSocialPost($id: ID!) {
    skipSocialPost(id: $id) { ${REACH_POST_FIELDS} }
  }
`;

export const APPROVE_CAMPAIGN = gql`
  mutation ApproveCampaign($id: ID!) {
    approveCampaign(id: $id) {
      approved
      scheduled
      undated
      skippedChannels
    }
  }
`;

export const REACH_CHANNELS = gql`
  query ReachChannels($accountId: ID!) {
    socialProfiles(accountId: $accountId) {
      id
      platform
      profileName
      profileUsername
      profileLink
      isActive
      isPublishing
      approvalMode
    }
  }
`;

export const SET_APPROVAL_MODE = gql`
  mutation SetSocialProfileApprovalMode($id: ID!, $mode: String!) {
    setSocialProfileApprovalMode(id: $id, mode: $mode) {
      id
      approvalMode
    }
  }
`;

export interface ReachPost {
  id: string;
  platform: string;
  summary?: string | null;
  title?: string | null;
  reachStatus: string;
  reviewStatus?: string | null;
  scheduleTime?: string | null;
  proposedTime?: string | null;
  socialPublishDate?: string | null;
  socialProfileId: string;
}

export interface Channel {
  id: string;
  platform: string;
  profileName?: string | null;
  profileUsername?: string | null;
  profileLink?: string | null;
  isActive: boolean;
  isPublishing?: boolean | null;
  approvalMode: 'ask' | 'auto' | string;
}

/** Platforms we can post to, in the order Add a channel shows them. */
export const POSTABLE = ['x', 'threads', 'instagram', 'facebook', 'youtube', 'tiktok'] as const;

const NAMES: Record<string, [string, string]> = {
  x: ['X', 'X'],
  twitter: ['X', 'X'],
  threads: ['Threads', '@'],
  instagram: ['Instagram', 'IG'],
  facebook: ['Facebook', 'f'],
  youtube: ['YouTube', 'YT'],
  tiktok: ['TikTok', 'TT'],
  linkedin: ['LinkedIn', 'in'],
  snapchat: ['Snapchat', 'SC'],
  bluesky: ['Bluesky', 'B'],
};
export const channelName = (p: string) => NAMES[p?.toLowerCase()]?.[0] || p;
export const channelMark = (p: string) =>
  NAMES[p?.toLowerCase()]?.[1] || p?.[0]?.toUpperCase() || '?';

/** Dates arrive as ISO strings or epoch milliseconds (as strings). */
export function toDate(v?: string | null): Date | null {
  if (!v) return null;
  const d = /^\d{10,}$/.test(v) ? new Date(Number(v)) : new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** When the calendar places a post: when it went out, will go out, or is proposed to. */
export const postTime = (p: ReachPost) =>
  toDate(p.socialPublishDate) || toDate(p.scheduleTime) || toDate(p.proposedTime);

export const postText = (p: ReachPost) => (p.summary || p.title || '').trim();

export const time = (d: Date) => d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
export const dayTime = (d: Date) =>
  d.toLocaleString([], {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
